/**
 * Core-100 CORE-AY — debounced universal search over both server sources (see lib/search/universalSearch).
 *
 * Plain state + effect (no react-query) so the TopBar that hosts the global search keeps working in every
 * tree it is rendered in. Only the latest request's answer is kept.
 */
import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
// Core-100 R4 wave 3 (CORE-AY D): the one customer-search request + reply reading (shared with the search bar).
import { runCustomerSearch } from '@/lib/search/customerSearchContract';
import { mergeUniversalResults, type UniversalMergeOptions, type UniversalSearchOutcome, type UniversalSearchRow } from '@/lib/search/universalSearch';
import type { CustomerSearchResult } from '@/lib/customers/types';

export async function runUniversalSearch(shopId: string, term: string, opts: UniversalMergeOptions = {}): Promise<UniversalSearchOutcome> {
  const [rpc, fn] = await Promise.allSettled([
    supabase.rpc('universal_search' as never, { p_shop_id: shopId, p_query: term, p_limit: 20 } as never),
    runCustomerSearch(supabase, shopId, term, 10),
  ]);
  const rows = rpc.status === 'fulfilled' && !rpc.value.error
    ? ((rpc.value.data ?? []) as UniversalSearchRow[]) : null;
  // R5 CORE-AY: universal_search carries the same per-user bound; over it the server raises PT429 (HTTP 429).
  const ticketsRefused = rpc.status === 'fulfilled' && (rpc.value.error as { code?: string } | null)?.code === 'PT429';
  const customers: CustomerSearchResult[] | null = fn.status === 'fulfilled' && fn.value.ok ? fn.value.results : null;
  // R4 CORE-AY: a refusal by the search bound is said as such ("too many searches"), never as "nothing found".
  const refused = fn.status === 'fulfilled' && fn.value.rateLimited === true;
  return mergeUniversalResults(rows, customers, {
    ...opts,
    customerFailure: fn.status === 'fulfilled' ? fn.value.reason : null,
    customerRateLimited: refused,
    ticketsRateLimited: ticketsRefused,
  });
}

export function useUniversalSearch(query: string, opts: { debounceMs?: number; enabled?: boolean; canOpenRoster?: boolean } = {}) {
  const { activeShop } = useAuth();
  const shopId = activeShop?.id;
  const enabled = opts.enabled ?? true;
  const [data, setData] = useState<UniversalSearchOutcome | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const requestId = useRef(0);

  useEffect(() => {
    const term = query.trim();
    if (!enabled || !shopId || term.length < 2) {
      setData(null);
      setIsLoading(false);
      return;
    }
    const id = ++requestId.current;
    setIsLoading(true);
    const t = setTimeout(() => {
      runUniversalSearch(shopId, term, { canOpenRoster: opts.canOpenRoster })
        .then((out) => { if (id === requestId.current) setData(out); })
        .finally(() => { if (id === requestId.current) setIsLoading(false); });
    }, opts.debounceMs ?? 250);
    return () => clearTimeout(t);
  }, [query, shopId, enabled, opts.debounceMs, opts.canOpenRoster]);

  return { data, isLoading, isFetching: isLoading };
}
